import React from 'react';
import {
    Responsive,
    SimpleList,
    SimpleShowLayout,
    Datagrid,
    TextField,
    BooleanField,
    SimpleForm,
    TextInput,
    SelectInput,
    NumberInput,
    maxValue,
    minValue,
    number,
    required,
} from 'admin-on-rest';
import List from '../admin/ListStatic';
import Show from '../admin/Show';
import Create from '../admin/Create';
import Edit from '../admin/Edit';
import ViewButton from '../buttons/ViewButton';
import EditButton from '../buttons/EditButton';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {passwordValidation, passwordChangeValidation} from "../shared/validation";
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/hardware/device-hub';
import FuseTheme from "../global/theme";

export const fuseSystemIcon = Icon;

const styles = {
    avatar: {backgroundColor: "#6e6e6e", width: 40, height: 40, marginRight: 10, verticalAlign: 'bottom'},
    description: {lineHeight: 1.5},
    row: {display: 'flex', justifyContent: 'space-between'},
    rowField: {flex: '0 1 30%', display: 'inline-block', margin: '0 1em 0 0', verticalAlign: 'top'},
    fieldWide: {flex: '0 1 80%', display: 'block', minWidth: '80%', maxWidth: '100%', overflow: 'hidden'},
    elFieldWide: {minWidth: '80%', maxWidth: '100%'},
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden'},
    fieldHeader: {
        display: 'block',
        width: '100%',
        fontSize: '16px',
        fontWeight: 'bold',
        color: FuseTheme.palette.lightTextColor,
        margin: '2em 0 0 0',
    },
    fieldInstructions: {
        display: 'block',
        width: '100%',
        fontSize: '14px',
        fontWeight: 'bold',
        color: FuseTheme.palette.lightTextColor,
        margin: '2em 0 2em 0',
    },
    fieldLabel: {
        display: 'block',
        width: '100%',
        fontSize: '14px',
        color: FuseTheme.palette.lightTextColor,
        margin: '2em 0 0 0',
    },
    fieldDescription: {
        flex: '1 1 40%',
        display: 'inline-block',
        width: 'auto',
        height: '2em',
        fontSize: '12px',
        color: FuseTheme.palette.lightTextColor,
        verticalAlign: 'bottom',
    },
    divider: {
        display: 'block',
        width: '100%',
        height: 1,
        backgroundColor: FuseTheme.palette.primary3Color,
        border: 'none',
        margin: '1em 0 0.5em 0'
    },
    hidden: {display: 'none'},
    break: {display: 'block', width: '100%', height: 0, border: 'none', margin: 0},
    paddingBottom: {paddingBottom: '1em'},
};

/**
 * Validate System Names
 *
 * @param value
 * @param allValues
 */
const nameValidation = (value, allValues) => {
    if (!value) {
        return 'A System Name is required';
    }
    const name = allValues.name;
    if (name.indexOf('"') !== -1) {
        return 'Name cannot contain quotation marks';
    }
    if (name.indexOf('\'') !== -1) {
        return 'Name cannot contain apostrophes';
    }
    if (name.indexOf(' ') !== -1) {
        return 'Name cannot contain spaces';
    }
    if (name.indexOf('&') !== -1) {
        return 'Name cannot contain ampersands';
    }
    if (name.indexOf(':') !== -1) {
        return 'Name cannot contain colons';
    }
    if (name.indexOf('?') !== -1) {
        return 'Name cannot contain question marks';
    }
    if (name !== name.toLowerCase()) {
        return 'Name cannot contain uppercase letters';
    }
    return null;
};

const validateName = [required, nameValidation];
const validatePassword = [required, passwordValidation];
const validateChangePassword = [passwordChangeValidation];

/*
 * View: List
 */

// Set title for List Locations
const ListTitle = <span><Avatar icon={<Icon/>} style={styles.avatar} className="avatar-title"/>Systems</span>;

const ListDescription = () => {
    return (
        <span>
        </span>
    );
};

/*
 * List Systems table
 */
export const fuseSystemList = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>}
          sort={{field: 'name', order: 'ASC'}}
          perPage={10}
    >
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.name}
                    secondaryText={record => record.active}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="name" label="Name"/>
                    <TextField source="display_name" label="Display Name"/>
                    <BooleanField source="active" label="Active"/>
                    <ViewButton/>
                    <EditButton/>
                </Datagrid>
            }
        />
    </List>
);

/*
 * View: Show
 */

// Set title for Show System
const ShowTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={styles.avatar}
                         className="avatar-title"/>{record ? `${record.display_name}` : 'System'}</span>;
};

const ShowDescription = () => {
    return (
        <span style={styles.description}>
            <p>Review system details below. To edit this System, click <strong>Edit</strong> above.</p>
        </span>
    );
};

/*
 * Show System Table
 */
export const fuseSystemShow = (props) => (
    <Show title={<ShowTitle/>} desc={<ShowDescription/>} {...props}>
        <SimpleShowLayout>
            <TextField source="name" label="Name"/>
            <TextField source="display_name" label="Display Name"/>
            <TextField source="description" label="Description"/>
            <TextField source="location_limit" label="Location Limit"/>
            <TextField source="device_limit" label="Device Limit"/>
            <BooleanField source="active" label="Active" style={styles.rowShowField} elStyle={styles.fieldSmall}/>
        </SimpleShowLayout>
    </Show>
);

/*
 * View: Create form
 */

// Set title for Create System
const CreateTitle = <span><Avatar icon={<Icon/>} style={styles.avatar}
                                  className="avatar-title"/>Create a System</span>;

const CreateDescription = () => {
    return (
        <span style={styles.description}>
            <p>Enter the required fields below.</p>
        </span>
    );
};

// Set defaults for Create form
const defaultCreateValues = {
    active: true,
    config: '',
    description: '',
    gp: 'Fuse',
    location_limit: 1,
    device_limit: 1
};

/*
 * Create System form
 */
export const fuseSystemCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm defaultValue={defaultCreateValues} submitOnEnter={false} redirect="list">
            <TextInput source="name" label="System Name" validate={validateName}
                       style={styles.fieldWide} elStyle={styles.elFieldWide}/>
            <span style={styles.fieldDescription}>
                Enter the <strong>Name</strong> for this System. Name must be URL-friendly.
                <TooltipIcon dataFor="ttName"/>
                <ReactTooltip id="ttName" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>System Name</strong> will be used to login to the CloudSent POS Admin and Reports.
                    <br/><br/>
                    The name must be a URL-friendly string with alphanumeric characters only: no spaces, quotation
                    marks, apostrophes, or ampersands.
                    <br/><br/>
                    Separate words with hyphens. Uppercase is allowed. Example: <em>Joes-Own-System</em>.
                </ReactTooltip>
            </span>

            <TextInput source="display_name" label="Display Name"
                       style={styles.fieldWide} elStyle={styles.elFieldWide}/>
            <span style={styles.fieldDescription}>
                Enter a <strong>Display Name</strong> for this System.
                <TooltipIcon dataFor="ttDisplayName"/>
                <ReactTooltip id="ttDisplayName" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Display Name</strong> is a human-friendly title for your System.
                    Example: <em>Joe's Own System</em>.
                    <br/><br/>
                    If you do not enter a Display Name, the System Name will be used.
                </ReactTooltip>
            </span>

            <TextInput source="sys_password" label="Password" type="password" validate={validatePassword}/>
            <TextInput source="sys_confirm_password" label="Confirm Password" type="password"
                       validate={validatePassword}/>
            <span style={styles.fieldDescription}>
                Enter a <strong>System Password</strong>.
                <TooltipIcon dataFor="ttPass"/>
                <ReactTooltip id="ttPass" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>System Password</strong> will be used to login to the CloudSent POS Admin and Reports.
                </ReactTooltip>
            </span>

            <TextInput source="description" label="Description" style={styles.fieldWide} elStyle={styles.elFieldWide}/>

            <NumberInput source="location_limit" label="Active Locations Limit" step={1}
                         validate={[required, number, minValue(1, 'Must be at least 1'), maxValue(100000, 'Cannot be more than 100,000')]}/>
            <span style={styles.fieldDescription}>
                Enter <strong>total active Locations allotted</strong> for this System.
                <TooltipIcon dataFor="ttLocationLimit"/>
                <ReactTooltip id="ttLocationLimit" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    This sets an expectation of how many active Locations are allotted for this System.
                    <br/><br/>
                    This does not limit the creation of new Locations; Sysadmins may create as many Locations as they
                    desire. This limit helps create reports to determine whether a System should be prompted to
                    upgrade, or charged for extra Locations beyond its plan.
                </ReactTooltip>
            </span>

            <NumberInput source="device_limit" label="POS Device Limit" step={1}
                         validate={[required, number, minValue(1, 'Must be at least 1'), maxValue(100000, 'Cannot be more than 100,000')]}/>
            <span style={styles.fieldDescription}>
                Enter <strong>total POS Devices allotted</strong> for this System.
                <TooltipIcon dataFor="ttDeviceLimit"/>
                <ReactTooltip id="ttDeviceLimit" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    This sets an expectation of how many POS Devices are assigned for this System.
                    <br/><br/>
                    Sysadmins may request as many Devices as they desire. This limit helps create reports to determine
                    whether a System should be prompted to upgrade, or charged for extra Devices used beyond its plan.
                </ReactTooltip>
            </span>

            <span style={styles.fieldLabel}>
                System is Active*
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Active</em>, this System may be available for Users.
                    <br/>
                    If <em>Inactive</em>, this System is unavailable for Users.
                </ReactTooltip>
            </span>
            <SelectInput source="active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'},
                         ]}
                         options={{
                             floatingLabelText: '',
                         }}
            />

            <span style={styles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Systems List.
            </span>
            <hr style={{...styles.break, ...styles.paddingBottom}}/>
        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit System
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={styles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.display_name}` : 'System'}</span>;
};

const EditDescription = () => {
    return (
        <span style={styles.description}>
            <p>Modify System Display Name or Description below. Click <strong>Save</strong> to update this System.</p>
        </span>
    );
};

/*
 * Edit System form
 */
export const fuseSystemEdit = (props) => (
    <Edit {...props} title={<EditTitle/>} desc={<EditDescription/>}>
        <SimpleForm submitOnEnter={false}>
            <TextField source="name" label="System Name"
                       style={styles.fieldWide} elStyle={styles.elFieldWide}/>
            <span style={styles.fieldDescription}>
                The <strong>Name</strong> of this System.
                <TooltipIcon dataFor="ttName"/>
                <ReactTooltip id="ttName" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>System Name</strong> is used to login to the CloudSent POS Admin and Reports. It
                    also the defines the unique name of the System. It cannot be edited.
                </ReactTooltip>
            </span>

            <TextInput source="display_name" label="Display Name"/>
            <span style={styles.fieldDescription}>
                Enter or update the <strong>Display Name</strong> for this System.
                <TooltipIcon dataFor="ttDisplayName"/>
                <ReactTooltip id="ttDisplayName" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Display Name</strong> is a human-friendly title for your System.
                    Example: <em>Joe's Own System</em>.
                    <br/><br/>
                    If you do not enter a Display Name, the System Name will be used.
                </ReactTooltip>
            </span>

            <TextInput source="description" label="Description"/>

            <hr style={{...styles.break, ...styles.paddingBottom}}/>

            <span style={styles.fieldInstructions}>
                Change System Password (if desired):
                <TooltipIcon dataFor="ttPass"/>
                <ReactTooltip id="ttPass" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>System Password</strong> is used to login to the CloudSent POS Admin and Reports. Modify
                    these fields only if you wish to save a new password.
                </ReactTooltip>
            </span>
            <TextInput source="sys_new_password" label="Change Password" type="password"
                       validate={validateChangePassword}/>
            <TextInput source="sys_confirm_new_password" label="Confirm Change Password" type="password"
                       validate={validateChangePassword}/>

            <NumberInput source="location_limit" label="Active Locations Limit" step={1}
                         validate={[required, number, minValue(1, 'Must be at least 1'), maxValue(100000, 'Cannot be more than 100,000')]}/>
            <span style={styles.fieldDescription}>
                Enter <strong>total active Locations allotted</strong> for this System.
                <TooltipIcon dataFor="ttLocationLimit"/>
                <ReactTooltip id="ttLocationLimit" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    This sets an expectation of how many active Locations are allotted for this System.
                    <br/><br/>
                    This does not limit the creation of new Locations; Sysadmins may create as many Locations as they
                    desire. This limit helps create reports to determine whether a System should be prompted to
                    upgrade, or charged for extra Locations beyond its plan.
                </ReactTooltip>
            </span>

            <NumberInput source="device_limit" label="POS Device Limit" step={1}
                         validate={[required, number, minValue(1, 'Must be at least 1'), maxValue(100000, 'Cannot be more than 100,000')]}/>
            <span style={styles.fieldDescription}>
                Enter <strong>total POS Devices allotted</strong> for this System.
                <TooltipIcon dataFor="ttDeviceLimit"/>
                <ReactTooltip id="ttDeviceLimit" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    This sets an expectation of how many POS Devices are assigned for this System.
                    <br/><br/>
                    Sysadmins may request as many Devices as they desire. This limit helps create reports to determine
                    whether a System should be prompted to upgrade, or charged for extra Devices used beyond its plan.
                </ReactTooltip>
            </span>

            <span style={styles.fieldLabel}>
                System is Active*
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Active</em>, this System may be available for Users.
                    <br/>
                    If <em>Inactive</em>, this System is unavailable for Users.
                </ReactTooltip>
            </span>
            <SelectInput source="active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'},
                         ]}
                         options={{
                             floatingLabelText: '',
                         }}
            />

            <TextInput source="password" style={styles.hidden} elStyle={styles.hidden}/>
            <TextInput source="config" style={styles.hidden} elStyle={styles.hidden}/>
            <TextInput source="gp" style={styles.hidden} elStyle={styles.hidden}/>

            <span style={styles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Systems List.
            </span>
            <hr style={{...styles.break, ...styles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/System/system.js